import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';

const CropVideo = () => {
  const { name, place, date, hour, videoName } = useParams();
  const navigate = useNavigate();

  // Estados dos Controles
  const [zoom, setZoom] = useState(1.0);
  const [format, setFormat] = useState('9:16');
  
  // Estados de Processamento
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Histórico de Crops (Virá do DynamoDB)
  const [existingCrops, setExistingCrops] = useState([]);

  // URL base do seu bucket oficial
  const S3_BASE_URL = "https://replaypro.com.br";
  const originalVideoUrl = `${S3_BASE_URL}${videoName}`;

  // Opções de formato (Sem 16:9)
  const formatOptions = [
    { value: "9:16", label: "📱 Vertical (9:16)" },
    { value: "4:5", label: "📱 Retrato (4:5)" },
    { value: "11:14", label: "📸 Otimizado (11:14)" },
    { value: "1:1", label: "⬛ Quadrado (1:1)" }
  ];

  // Função para formatar a data (Ex: 2026-04-08 para 08/Abr)
  const getFormattedDate = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    if (parts.length !== 3) return dateString;
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${parts[2]}/${months[parseInt(parts[1], 10) - 1]}`;
  };

  // Função para extrair a hora exata do final do arquivo
  const getExactTimeFromFilename = (filename) => {
    if (!filename) return `${hour}:00`;
    const match = filename.match(/_(\d{2}:\d{2}:\d{2})\.mp4$/);
    return match ? match[1] : `${hour}:00`;
  };

  // Função para transformar URLs absolutas em relativas (Força o Download de Same-Origin)
  const getRelativeUrl = (absoluteUrl) => {
    if (!absoluteUrl) return '#';
    try {
      return new URL(absoluteUrl).pathname;
    } catch {
      return absoluteUrl; 
    }
  };

  const formattedDate = getFormattedDate(date);
  const exactTime = getExactTimeFromFilename(videoName);

  // Envolvemos a função com useCallback para ela não ser recriada a cada renderização
  const fetchExistingCrops = useCallback(async () => {
    try {
      const safeVideoName = videoName.replace(/^\/+/, '');
      const API_URL = `https://1jfp6fzlxf.execute-api.us-east-1.amazonaws.com/items/crop?video=${safeVideoName}`; 

      const response = await fetch(API_URL);
      
      if (response.ok) {
        const data = await response.json();
        
        // Ordenamos os crops para que os mais novos apareçam primeiro
        const sortedData = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setExistingCrops(sortedData); 
      }
    } catch (error) {
      console.error("Falha na requisição de crops:", error);
    }
  }, [videoName]); // A função depende do videoName

  // Auto-Refresh a cada 10 segundos
  useEffect(() => {
    fetchExistingCrops(); // Busca imediatamente ao entrar
    
    const interval = setInterval(() => {
      fetchExistingCrops();
    }, 10000); // 10.000 ms = 10 segundos

    // Limpa o timer quando o usuário sair da tela
    return () => clearInterval(interval);
  }, [fetchExistingCrops]); // O useEffect agora depende da função "memorizada"

  const handleGenerateCrop = async () => {
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const API_URL = "https://1jfp6fzlxf.execute-api.us-east-1.amazonaws.com/items/crop"; 

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filePath: videoName.replace(/^\/+/, ''),
          zoom: parseFloat(zoom),
          format: format
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: 'Processamento iniciado! O vídeo será gerado pela IA em instantes.' 
        });
        
        // Força uma busca imediata para o Skeleton já aparecer na tela na mesma hora
        fetchExistingCrops();
      } else {
        throw new Error(data.error || 'Erro ao processar vídeo.');
      }
    } catch (error) {
      setMessage({ type: 'danger', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-4">
      {/* CSS CUSTOMIZADO PARA ESTA TELA */}
      <style>
        {`
          .btn-ai-generate {
            width: 100%;
          }
          @media (min-width: 992px) {
            .btn-ai-generate {
              width: fit-content;
              padding-left: 2rem;
              padding-right: 2rem;
            }
          }
          .video-skeleton {
            height: 350px;
            background-color: #1a1a1a;
            border-radius: 6px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            color: #fff;
          }
        `}
      </style>

      {/* BREADCRUMB / NAVEGAÇÃO */}
      <div className="mb-4">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left"></i> Voltar para os Replays
        </Button>
        <h4 className="mt-3">
          Recorte Inteligente (IA)
        </h4>
        <p className="text-muted">
          {decodeURIComponent(name)} - {decodeURIComponent(place)} | {formattedDate} - {hour}:00 - {hour}:59
        </p>
      </div>

      <Row>
        {/* COLUNA CENTRALIZADA (Vídeo + Controles) */}
        <Col xs={12} lg={8} className="mx-auto mb-4">
          
          <Card className="flex-fill replay-card shadow-sm mb-4">
            <Card.Body>
              {/* VÍDEO ORIGINAL */}
              <Card.Title className="arena-logo mb-0">
                <video className="w-100" controls style={{ maxHeight: '500px', backgroundColor: '#000' }}>
                  <source src={originalVideoUrl} type="video/mp4" />
                  Seu navegador não suporta vídeos.
                </video>
              </Card.Title>
              
              <Card.Subtitle className="replay-download d-flex justify-content-between align-items-center mt-3">
                <span className="font-weight-bold">{exactTime}</span>
                <span>
                  <a href={getRelativeUrl(originalVideoUrl)} download>
                    Download Original
                  </a>
                </span>
              </Card.Subtitle>

              <hr className="my-4" />

              {/* CONTROLES DE RECORTE DENTRO DO CARD */}
              <h5 className="mb-4 font-weight-bold" style={{ fontSize: '1.1rem' }}>
                Configurações de Recorte
              </h5>
              
              <Form>
                {/* FORMATO COMO TAGS */}
                <Form.Group className="mb-4">
                  <Form.Label className="font-weight-bold text-muted small text-uppercase">Formato</Form.Label>
                  <div className="d-flex flex-wrap">
                    {formatOptions.map((opt) => (
                      <Button
                        key={opt.value}
                        variant={format === opt.value ? 'dark' : 'outline-secondary'}
                        onClick={() => setFormat(opt.value)}
                        disabled={isLoading}
                        className="rounded-pill border px-3 py-1 mr-2 mb-2"
                        style={{ cursor: 'pointer', fontSize: '0.9rem' }}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </Form.Group>

                {/* SLIDER DE ZOOM */}
                <Form.Group className="mb-4">
                  <Form.Label className="font-weight-bold text-muted small text-uppercase d-flex justify-content-between w-100">
                    <span>Zoom</span>
                    <span className="text-danger font-weight-bold">{zoom}x</span>
                  </Form.Label>
                  <Form.Range 
                    className="w-100"
                    min={1.0} 
                    max={1.5} 
                    step={0.1} 
                    value={zoom} 
                    onChange={(e) => setZoom(e.target.value)}
                    disabled={isLoading}
                    style={{ cursor: 'pointer' }}
                  />
                  <Form.Text className="text-muted">
                    Ajuste o zoom para aproximar a câmera da bola.
                  </Form.Text>
                </Form.Group>

                {/* BOTÃO GERAR (100% Mobile, Direita no Desktop) */}
                <div className="text-lg-right mt-4">
                  <Button 
                    variant="danger" 
                    onClick={handleGenerateCrop}
                    disabled={isLoading}
                    className="btn-ai-generate font-weight-bold"
                  >
                    {isLoading ? (
                      <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Iniciando IA...</>
                    ) : (
                      <>✨ Gerar Recorte com IA</>
                    )}
                  </Button>
                </div>
              </Form>

              {/* MENSAGENS DE FEEDBACK */}
              {message.text && (
                <Alert variant={message.type} className="mt-3 mb-0">
                  {message.text}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* VÍDEOS CORTADOS GERADOS LOGO ABAIXO */}
          {existingCrops.length > 0 && (
            <div className="mt-5">
              <h5 className="mb-3 text-muted">
                <i className="bi bi-scissors"></i> Recortes Gerados
              </h5>
              <Row>
                {existingCrops.map((crop, index) => (
                  <Col md={6} key={index} className="mb-4">
                    <Card className="flex-fill replay-card shadow-sm h-100">
                      <Card.Body className="d-flex flex-column">
                        
                        {/* RENDERIZAÇÃO CONDICIONAL POR STATUS */}
                        {crop.status === 'PROCESSING' && (
                          <div className="video-skeleton">
                            <Spinner animation="grow" variant="danger" />
                            <h6 className="mt-3 font-weight-bold">Processando Vídeo...</h6>
                            <p className="text-muted small text-center px-3 mb-0">A Inteligência Artificial está acompanhando a bola para montar o recorte.</p>
                          </div>
                        )}

                        {crop.status === 'ERROR' && (
                          <div className="video-skeleton border border-danger">
                            <i className="bi bi-exclamation-triangle-fill text-danger mb-2" style={{ fontSize: '2.5rem' }}></i>
                            <h6 className="mt-2 font-weight-bold">Falha no Processamento</h6>
                            <p className="text-muted small text-center px-3 mb-0">Houve um erro técnico no processamento ou a bola não foi identificada no vídeo.</p>
                          </div>
                        )}

                        {crop.status === 'DONE' && (
                          <Card.Title className="arena-logo mb-0">
                            <video className="w-100" controls style={{ maxHeight: '350px', backgroundColor: '#000' }}>
                              <source src={crop.url} type="video/mp4" />
                              Seu navegador não suporta vídeos.
                            </video>
                          </Card.Title>
                        )}
                        
                        {/* RODAPÉ DO CARD */}
                        <Card.Subtitle className="replay-download d-flex justify-content-between align-items-center mt-auto pt-3">
                          <span className="font-weight-bold text-dark" style={{ fontSize: '0.9rem' }}>
                            {formatOptions.find(o => o.value === crop.format)?.label.split(' ')[1] || crop.format} | {crop.zoom}x
                          </span>
                          
                          <span>
                            {crop.status === 'DONE' && (
                              <a href={getRelativeUrl(crop.url)} download>
                                Download
                              </a>
                            )}
                            {crop.status === 'PROCESSING' && (
                              <span className="badge badge-warning text-dark px-2 py-1">Processando</span>
                            )}
                            {crop.status === 'ERROR' && (
                              <span className="badge badge-danger px-2 py-1">Erro</span>
                            )}
                          </span>
                        </Card.Subtitle>

                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default CropVideo;